import moment, { type Moment } from 'moment'
import { NativeEventEmitter, NativeModules } from 'react-native'
import AppleHealthKit, { type AnchoredQueryResults } from 'react-native-health'
import * as base64 from 'base-64'
import { WellnessCenter } from '@dialogue/services'
import { transformDateLimit, formatDateToISOString } from './DateHelpers'
import AsyncStorage from '@react-native-async-storage/async-storage'
import I18n from 'APP/I18n'
import { logger } from 'APP/Lib/Datadog'
export * from 'react-native-health'

const { ActivityName, MetricName } = WellnessCenter.Challenges.Types

type MetricNameType = (typeof MetricName)[keyof typeof MetricName]
type DateInput = string | Date | Moment

interface DailySample {
  startDate: string
  endDate: string
  value: number
}

interface SampleOptions {
  startDate: string
  endDate: string
}

interface AddMetricsParams {
  startDate: string
  endDate: string
  fromDate: string
  toDate: string
}

interface Metric {
  metric: MetricNameType
  value: number
}

interface Activity {
  activity: string
  activity_time: string
  unique_id: string
  metrics: Metric[]
}

interface HealthKitActivitiesCache {
  [date: string]: Activity[]
}

export const getDailyStepCountSamplesAsync = (options: SampleOptions): Promise<DailySample[]> => {
  return new Promise((resolve, reject) => {
    AppleHealthKit.getDailyStepCountSamples(options, (err, results) => {
      return err ? reject(err) : resolve(results)
    })
  })
}

export const getAnchoredWorkoutsAsync = (options: SampleOptions): Promise<AnchoredQueryResults> => {
  return new Promise((resolve, reject) => {
    AppleHealthKit.getAnchoredWorkouts(options, (err, results) => {
      return err ? reject(err) : resolve(results)
    })
  })
}

export const transformWorkoutOrSampleToMetrics = (
  metric: MetricNameType,
  { start, end, value }: { start: DateInput; end: DateInput; value: number }
): Metric => {
  const handler: Record<MetricNameType, () => Metric> = {
    [MetricName.STEPS]: () => {
      return {
        metric: MetricName.STEPS,
        value: Math.max(1, Math.round(value)),
      }
    },
    [MetricName.DISTANCE]: () => {
      return {
        metric: MetricName.DISTANCE,
        value: Math.max(1, Math.round(value)),
      }
    },
    [MetricName.DURATION]: () => {
      return {
        metric: MetricName.DURATION,
        value: Math.max(moment(end).diff(moment(start), 'minutes'), 1),
      }
    },
    [MetricName.CALORIES]: () => {
      return {
        metric: MetricName.CALORIES,
        value: Math.max(1, Math.round(value)),
      }
    },
  }
  return handler[metric]()
}

export const makeTransformDailySamplesToActivities = (
  activity: string,
  metrics: MetricNameType[]
) => {
  return (samples: DailySample[]): Activity[] => {
    return samples.map(({ startDate, endDate, value }) => ({
      activity: activity,
      activity_time: formatDateToISOString(startDate),
      unique_id: base64.encode(`${activity}:${startDate}:${endDate}:${value}`),
      metrics: metrics.map((m) =>
        transformWorkoutOrSampleToMetrics(m, { start: startDate, end: endDate, value })
      ),
    }))
  }
}

/**
 * Transform samples options to be compatible with `addMetricsTracker`.
 * Apple Health Kit doesn't take into consideration milliseconds.
 * When a request sample is made with a date range with milliseconds as `2022-03-07 22:29:09.069000`.
 * Returns can contain an activity happening at `2022-03-07 22:29:09.010000` and `addMetricsTracker` request returns a 400.
 * To prevent this error a cast is made to transform `fromDate|toDate` to `2022-03-07 22:29:09.000|2022-03-07 22:29:09.999`.
 * This ensure no out of bounds range.
 * @param {Object} options
 * @param {string} options.startDate - startDate of a sample.
 * @param {string} options.endDate - endDate of a sample.
 * @returns {Object} addMetricsParams
 * @returns {string} addMetricsParam.startDate - ISO Date.
 * @returns {string} addMetricsParam.endDate - ISO Date.
 * @returns {string} addMetricsParam.fromDate - ISO Date, cast to the start of a second.
 * @returns {string} addMetricsParam.toDate - ISO Date, cast to the end of a second.
 */
export const transformSamplesOptionsToAddMetricsParams = (
  options: Partial<SampleOptions> = {}
): AddMetricsParams => {
  return {
    startDate: formatDateToISOString(options.startDate),
    endDate: formatDateToISOString(options.endDate),
    fromDate: moment(formatDateToISOString(options.startDate)).startOf('second').toISOString(),
    toDate: transformDateLimit(options.endDate, moment()).endOf('second').toISOString(),
  }
}

export const transformStepsCountSamplesToActivities = makeTransformDailySamplesToActivities(
  ActivityName.STEPS,
  [MetricName.STEPS]
)

export const convertMilesToMeters = (miles: number): number => {
  return miles * 1609.344
}

export const transformDailyAnchoredWorkoutSamples = (
  { anchor, data }: AnchoredQueryResults,
  activities: string[]
): Activity[] => {
  if (!data) return []

  return data
    .filter(({ distance, activityName }) => {
      return distance > 0 && activities.includes(activityName.toLowerCase())
    })
    .map(({ start, end, distance, activityName }) => ({
      activity: activityName.toLowerCase(),
      activity_time: formatDateToISOString(start),
      unique_id: base64.encode(`${anchor}:${activityName}:${start}:${end}`),
      metrics: [
        {
          metric: MetricName.DISTANCE,
          value: Math.max(1, Math.round(convertMilesToMeters(distance))),
        },
        {
          metric: MetricName.DURATION,
          value: Math.max(moment(end).diff(moment(start), 'minutes'), 1),
        },
      ],
    }))
}

export const getDailyStepCountActivitySamples = async (
  options: SampleOptions
): Promise<Activity[]> => {
  const samples = await getDailyStepCountSamplesAsync(options)
  return transformStepsCountSamplesToActivities(samples)
}

export const getDailyAnchorActivitySamples = async (
  options: SampleOptions,
  activities: string[]
): Promise<Activity[]> => {
  const samples = await getAnchoredWorkoutsAsync(options)
  return transformDailyAnchoredWorkoutSamples(samples, activities)
}

export const getAllActivitySamples = async (options: SampleOptions): Promise<Activity[]> => {
  const { startDate, endDate } = transformSamplesOptionsToAddMetricsParams(options)

  const samples = await Promise.all([
    getDailyStepCountActivitySamples({ startDate, endDate }),
    getDailyAnchorActivitySamples({ startDate, endDate }, [
      ActivityName.RUNNING,
      ActivityName.SWIMMING,
      ActivityName.CYCLING,
    ]),
  ])

  return samples.flatMap((a) => a)
}

/**
 * Register a listener to react on new data.
 */
export const listenToAllActivities = (
  callback: (error: null, activity: string) => void
): (() => void) => {
  const activities = ['Cycling', 'Running', 'StepCount', 'Swimming', 'Walking', 'Workout']

  const listeners = activities.map((activity) => {
    return new NativeEventEmitter(NativeModules.AppleHealthKit).addListener(
      `healthKit:${activity}:new`,
      () => callback(null, activity)
    )
  }, [])

  return () => {
    listeners.forEach((listener) => listener.remove())
  }
}

// If HealthKit cache needs to be reset
// bump the version ONLY
const HK_CACHE_VERSION = '1'
const HK_CACHE_PREFIX = 'healthKitActivitiesCache'
export const HK_CACHE_KEY_NAME = `${HK_CACHE_PREFIX}.v${HK_CACHE_VERSION}`

export async function cleanupUnusedCache(): Promise<void> {
  try {
    const allASKeys = await AsyncStorage.getAllKeys()
    const HKUnusedKeys = allASKeys.filter(
      (key) => key !== HK_CACHE_KEY_NAME && key.startsWith(HK_CACHE_PREFIX)
    )
    if (!HKUnusedKeys.length) return
    await AsyncStorage.multiRemove(HKUnusedKeys)
  } catch (error) {
    logger.error('Failed to clean up unused HealthKit cache', {}, error)
  }
}
cleanupUnusedCache()

export const limitCache = (
  healthKitActivitiesCache: HealthKitActivitiesCache | null
): HealthKitActivitiesCache => {
  const dateFormat = I18n.t('DateFormat')
  const CACHE_LIMIT_DATE = moment().subtract(3, 'months').format(dateFormat)

  if (!healthKitActivitiesCache) return {}

  const sortedCache = Object.fromEntries(Object.entries(healthKitActivitiesCache).sort())
  const CACHE_ACTIVITIES_LIMIT = 1012

  Object.keys(sortedCache).every((date) => {
    if (
      moment(date).isBefore(CACHE_LIMIT_DATE) ||
      Object.values(sortedCache).reduce((length, activities) => length + activities.length, 0) >
        CACHE_ACTIVITIES_LIMIT
    ) {
      delete sortedCache[date]
      return true
    }
    return false
  })

  return sortedCache
}

export const setHealthKitCache = (healthKitActivitiesCache: HealthKitActivitiesCache): void => {
  const limitedCache = limitCache(healthKitActivitiesCache)
  AsyncStorage.setItem(HK_CACHE_KEY_NAME, JSON.stringify(limitedCache))
}

export const getHealthKitCache = async (): Promise<HealthKitActivitiesCache> => {
  const healthKitActivitiesCache = await AsyncStorage.getItem(HK_CACHE_KEY_NAME)
  return limitCache(healthKitActivitiesCache ? JSON.parse(healthKitActivitiesCache) : null)
}

export const clearHealthKitCache = (): Promise<void> => {
  return AsyncStorage.removeItem(HK_CACHE_KEY_NAME)
}
